import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from '../hooks/useTranslation';
import { Card, Button, AlertBadge, Badge, Spinner } from '../components/ui';

function timeAgo(
  date: Date | string,
  t: (key: any) => string,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const suffix = t('dashboard.timeAgoSuffix');
  if (diffMin < 1) return t('dashboard.timeJustNow');
  if (diffMin < 60) return `${diffMin} ${t('dashboard.timeMinAgo')}`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) {
    const unit = diffH === 1 ? t('dashboard.timeHourAgo') : t('dashboard.timeHoursAgo');
    return `${diffH} ${unit} ${suffix}`;
  }
  const diffD = Math.floor(diffH / 24);
  const unit = diffD === 1 ? t('dashboard.timeDayAgo') : t('dashboard.timeDaysAgo');
  return `${diffD} ${unit} ${suffix}`;
}

export default function Notifications() {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    refetch,
  } = useNotifications();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button variant="outline" onClick={() => refetch()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
            {t('notifications.title')}
          </h1>
          {unreadCount > 0 && (
            <Badge variant="info" size="sm">
              {unreadCount} {unreadCount === 1 ? t('notifications.newSingular') : t('notifications.newPlural')}
            </Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllAsRead}>
            {t('notifications.markAllRead')}
          </Button>
        )}
      </div>

      {/* Notifications list */}
      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-6 w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            {t('notifications.empty')}
          </h2>
          <p className="text-text-secondary max-w-sm">
            {t('notifications.emptyDescription')}
          </p>
        </div>
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-border">
            {notifications.map((notif) => (
              <li key={notif.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!notif.letta) markAsRead(notif.id);
                  }}
                  className={`w-full text-left flex items-start gap-3 px-4 py-4 hover:bg-surface-secondary transition-colors cursor-pointer ${
                    !notif.letta ? 'bg-primary-50/50' : ''
                  }`}
                >
                  {/* Unread dot */}
                  <div className="shrink-0 mt-2 w-2 h-2">
                    {!notif.letta && (
                      <div className="w-2 h-2 rounded-full bg-primary-500" />
                    )}
                  </div>

                  {/* Alert badge */}
                  <div className="shrink-0 mt-0.5">
                    <AlertBadge level={notif.tipo} />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm ${
                          !notif.letta
                            ? 'font-semibold text-text-primary'
                            : 'text-text-primary'
                        }`}
                      >
                        {notif.titolo}
                      </p>
                      <span className="text-xs text-text-secondary shrink-0 whitespace-nowrap">
                        {notif.inviataAt ? timeAgo(notif.inviataAt, t) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary mt-1">
                      {notif.corpo}
                    </p>
                    {notif.actionUrl && (
                      <div className="mt-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(notif.actionUrl!, '_blank', 'noopener');
                          }}
                        >
                          {t('notifications.bookNow')}
                        </Button>
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {/* Load more */}
          {hasMore && (
            <div className="px-4 py-4 text-center border-t border-border">
              <Button variant="ghost" onClick={loadMore}>
                {t('notifications.loadMore')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
